'use client';

import { decode } from 'html-entities';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { styles } from './ProductDescription.styles';

export interface ProductDescriptionProps {
  html: string;
}

export function ProductDescription({ html }: ProductDescriptionProps) {
  const t = useTranslations();
  const isMobile = useMediaQuery('lg', 'max');
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);
  const slots = styles({ expanded: isExpanded });
  const decodedHtml = decode(html);

  useEffect(() => {
    if (!shouldScrollRef.current) return;

    requestAnimationFrame(() => {
      utilScrollToElement(baseRef.current, { offset: 16 });
      shouldScrollRef.current = false;
    });
  }, [isExpanded]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || isExpanded) return;

    const checkOverflow = () => {
      if (content.scrollHeight > content.clientHeight + 1) {
        setHasOverflow(true);
      }
    };

    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(content);

    return () => observer.disconnect();
  }, [decodedHtml, isExpanded, isMobile]);

  return (
    <div
      ref={baseRef}
      className={slots.base()}
    >
      <div
        ref={contentRef}
        className={slots.content()}
        dangerouslySetInnerHTML={{ __html: decodedHtml }}
        suppressHydrationWarning
      />
      {hasOverflow && (
        <UiKitButtonBase
          className={slots.button()}
          onClick={() => {
            if (isMobile) {
              shouldScrollRef.current = true;
            }
            setIsExpanded((expanded) => !expanded);
          }}
          color="ghost"
        >
          {isExpanded ? t('hide') : t('more')}
          <ChevronIcon
            className={slots.buttonIcon({
              className: isExpanded ? '-rotate-90' : 'rotate-90'
            })}
          />
        </UiKitButtonBase>
      )}
    </div>
  );
}
